import type { SubjectTimeSeriesTooltipProps } from 'features/knowledgeDocumentViewer/components/viewer/shared/SubjectTimeSeriesTooltip'
import { useDocumentTabDataContextOptional } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useTimeSeriesLinkContext } from 'features/knowledgeDocumentViewer/contexts/TimeSeriesLinkContext'
import type { AliasHighlight } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import { matchSubjectDatapoints } from 'features/knowledgeDocumentViewer/utils/instrumentTagUtils'
import React, { useCallback, useMemo, useState } from 'react'

/**
 * Hover state for subject alias highlights. Wire `handleAliasHover` into the
 * overlay's `onAliasHover`; when `aliasTooltipProps` is non-null, render a
 * `SubjectTimeSeriesTooltip` inside the positioned ancestor `containerRef`
 * points at (positions are measured against it).
 */
export function useAliasTooltip({
  showTooltips,
  containerRef,
}: {
  showTooltips: boolean
  containerRef: React.RefObject<HTMLElement | null>
}) {
  const tabData = useDocumentTabDataContextOptional()
  const { getMatchedDatapoints } = useTimeSeriesLinkContext()

  const [aliasTooltip, setAliasTooltip] = useState<{
    highlight: AliasHighlight
    x: number
    y: number
  } | null>(null)

  const handleAliasHover = useCallback(
    (highlight: AliasHighlight | null, event?: React.MouseEvent) => {
      if (!highlight || !event || !showTooltips || !containerRef.current) {
        setAliasTooltip(null)
        return
      }
      const containerRect = containerRef.current.getBoundingClientRect()
      setAliasTooltip({
        highlight,
        x: event.clientX - containerRect.left,
        y: event.clientY - containerRect.top,
      })
    },
    [showTooltips, containerRef]
  )

  const subject = useMemo(
    () =>
      aliasTooltip
        ? tabData?.documentSubjects.find(
            (s) => s.subject_uid === aliasTooltip.highlight.subjectUid
          )
        : undefined,
    [aliasTooltip, tabData?.documentSubjects]
  )

  const datapoints = useMemo(
    () => matchSubjectDatapoints(subject, getMatchedDatapoints),
    [subject, getMatchedDatapoints]
  )

  const aliasTooltipProps: SubjectTimeSeriesTooltipProps | null =
    showTooltips && aliasTooltip && datapoints.length > 0
      ? {
          label: subject?.name ?? aliasTooltip.highlight.label,
          matchedDatapoints: datapoints,
          tooltipPosition: { x: aliasTooltip.x, y: aliasTooltip.y },
          viewportWidth: containerRef.current?.clientWidth ?? 1200,
          viewportHeight: containerRef.current?.clientHeight ?? 800,
        }
      : null

  return { handleAliasHover, aliasTooltipProps }
}
